import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material';
import { ResponsiveLine } from '@nivo/line';
import { useChartTheme } from '../../themes/chartTheme';
import { determineAggregation, getFormattedTick, AggregationType } from '../../helpers/chartUtils';
import { formatNumber } from '../../helpers/numberUtils';
import { fetchConversions, ConversionRow, ConversionTotals } from '../../helpers/adEvaluationData';
import moment from 'moment';

const ConversionsTab: React.FC = () => {
  const chartTheme = useChartTheme();

  const [startDate, setStartDate] = useState(moment().subtract(3, 'months').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  const [loading, setLoading] = useState(true);
  const [timeline, setTimeline] = useState<ConversionRow[]>([]);
  const [totals, setTotals] = useState<ConversionTotals | null>(null);
  const [error, setError] = useState<string | null>(null);

  const aggregation = useMemo((): AggregationType => {
    return determineAggregation(new Date(startDate), new Date(endDate));
  }, [startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchConversions(new Date(startDate), new Date(endDate), aggregation);
        if (!cancelled) {
          setTimeline(result.timeline);
          setTotals(result.totals);
        }
      } catch (err) {
        console.error('Error fetching conversions:', err);
        if (!cancelled) setError('Fehler beim Laden der Conversion-Daten');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, aggregation]);

  const chartData = useMemo(() => {
    if (timeline.length === 0) return [];
    return [
      {
        id: 'DOI Regs',
        color: '#2196f3',
        data: timeline.map((r) => ({
          x: r.period,
          y: Number(r.doi_webinar_regs) || 0,
        })),
      },
      {
        id: 'DOI Teilnehmer',
        color: '#90caf9',
        data: timeline.map((r) => ({
          x: r.period,
          y: Number(r.doi_webinar_attendees) || 0,
        })),
      },
      {
        id: 'DOI PLX',
        color: '#f44336',
        data: timeline.map((r) => ({
          x: r.period,
          y: Number(r.doi_plx_orders) || 0,
        })),
      },
      {
        id: 'ART Regs',
        color: '#4caf50',
        data: timeline.map((r) => ({
          x: r.period,
          y: Number(r.article_webinar_regs) || 0,
        })),
      },
      {
        id: 'ART Teilnehmer',
        color: '#a5d6a7',
        data: timeline.map((r) => ({
          x: r.period,
          y: Number(r.article_webinar_attendees) || 0,
        })),
      },
      {
        id: 'ART PLX',
        color: '#ff9800',
        data: timeline.map((r) => ({
          x: r.period,
          y: Number(r.article_plx_orders) || 0,
        })),
      },
    ];
  }, [timeline]);

  return (
    <Box>
      {/* Date inputs */}
      <Box display="flex" gap={2} mb={3}>
        <TextField
          label="Von"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="Bis"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <Typography variant="body2" color="text.secondary" alignSelf="center">
          Aggregation:{' '}
          {aggregation === 'daily'
            ? 'Täglich'
            : aggregation === 'weekly'
              ? 'Wöchentlich'
              : 'Monatlich'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary cards — 2 rows of 4 */}
      {totals && (
        <>
          <Typography variant="subtitle2" color="text.secondary" mb={1}>
            DOI-Seite
          </Typography>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={6} md={3}>
              <SummaryCard
                label="Webinar Regs"
                tooltip="Webinar-Registrierungen über DOI-Seiten-Anzeigen"
                value={formatNumber(totals.doi_webinar_regs, 0)}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <SummaryCard
                label="Webinar Teilnehmer"
                tooltip="Tatsächliche Webinar-Teilnehmer aus DOI-Registrierungen"
                value={formatNumber(totals.doi_webinar_attendees, 0)}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <SummaryCard
                label="PLX Bestellungen"
                tooltip="PLX-Bestellungen (Status: PROCESSING) über DOI-Seiten-Anzeigen"
                value={formatNumber(totals.doi_plx_orders, 0)}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <SummaryCard
                label="PLX Umsatz"
                tooltip="Umsatz aus PLX-Bestellungen über DOI-Seiten-Anzeigen"
                value={formatNumber(totals.doi_plx_revenue, 2) + ' €'}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" color="text.secondary" mb={1}>
            Artikel-Anzeigen
          </Typography>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} md={3}>
              <SummaryCard
                label="Webinar Regs"
                tooltip="Webinar-Registrierungen über Artikel-Anzeigen"
                value={formatNumber(totals.article_webinar_regs, 0)}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <SummaryCard
                label="Webinar Teilnehmer"
                tooltip="Tatsächliche Webinar-Teilnehmer aus Artikel-Registrierungen"
                value={formatNumber(totals.article_webinar_attendees, 0)}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <SummaryCard
                label="PLX Bestellungen"
                tooltip="PLX-Bestellungen (Status: PROCESSING) über Artikel-Anzeigen"
                value={formatNumber(totals.article_plx_orders, 0)}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <SummaryCard
                label="PLX Umsatz"
                tooltip="Umsatz aus PLX-Bestellungen über Artikel-Anzeigen"
                value={formatNumber(totals.article_plx_revenue, 2) + ' €'}
              />
            </Grid>
          </Grid>
        </>
      )}

      {/* Chart */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : timeline.length === 0 ? (
        <Alert severity="info">Keine Conversions im gewählten Zeitraum.</Alert>
      ) : (
        <>
          <Box height={400} mb={3}>
            <ResponsiveLine
              data={chartData}
              theme={chartTheme}
              margin={{ top: 20, right: 150, bottom: 50, left: 60 }}
              xScale={{ type: 'point' }}
              yScale={{ type: 'linear', min: 0, stacked: false }}
              axisBottom={{
                tickRotation: -45,
                format: (v) => getFormattedTick(aggregation, v),
              }}
              axisLeft={{
                legend: 'Anzahl',
                legendOffset: -50,
                legendPosition: 'middle',
              }}
              pointSize={6}
              pointBorderWidth={2}
              pointBorderColor={{ from: 'serieColor' }}
              useMesh={true}
              colors={{ datum: 'color' }}
              legends={[
                {
                  anchor: 'bottom-right',
                  direction: 'column',
                  translateX: 140,
                  itemWidth: 130,
                  itemHeight: 20,
                  symbolSize: 12,
                  symbolShape: 'circle',
                },
              ]}
              tooltip={({ point }) => (
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    p: 1,
                    border: '1px solid #ccc',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="caption" fontWeight="bold">
                    {point.serieId}
                  </Typography>
                  <br />
                  <Typography variant="caption">
                    {String(point.data.x)}: {Number(point.data.y)}
                  </Typography>
                </Box>
              )}
            />
          </Box>

          {/* Detail table */}
          <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <HeaderCell>Zeitraum</HeaderCell>
                  <HeaderCell align="right" tooltip="Webinar-Registrierungen über DOI-Seiten">
                    DOI Regs
                  </HeaderCell>
                  <HeaderCell
                    align="right"
                    tooltip="Tatsächliche Webinar-Teilnehmer aus DOI-Registrierungen"
                  >
                    DOI Teiln.
                  </HeaderCell>
                  <HeaderCell
                    align="right"
                    tooltip="PLX-Bestellungen (Status: PROCESSING) über DOI-Seiten"
                  >
                    DOI PLX
                  </HeaderCell>
                  <HeaderCell align="right" tooltip="Umsatz aus PLX-Bestellungen über DOI-Seiten">
                    DOI Umsatz
                  </HeaderCell>
                  <HeaderCell align="right" tooltip="Webinar-Registrierungen über Artikel-Anzeigen">
                    ART Regs
                  </HeaderCell>
                  <HeaderCell
                    align="right"
                    tooltip="Tatsächliche Webinar-Teilnehmer aus Artikel-Registrierungen"
                  >
                    ART Teiln.
                  </HeaderCell>
                  <HeaderCell
                    align="right"
                    tooltip="PLX-Bestellungen (Status: PROCESSING) über Artikel-Anzeigen"
                  >
                    ART PLX
                  </HeaderCell>
                  <HeaderCell
                    align="right"
                    tooltip="Umsatz aus PLX-Bestellungen über Artikel-Anzeigen"
                  >
                    ART Umsatz
                  </HeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {timeline.map((row) => (
                  <TableRow key={row.period}>
                    <TableCell>{row.period}</TableCell>
                    <TableCell align="right">
                      {formatNumber(Number(row.doi_webinar_regs), 0)}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(Number(row.doi_webinar_attendees), 0)}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(Number(row.doi_plx_orders), 0)}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(Number(row.doi_plx_revenue), 2)} €
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(Number(row.article_webinar_regs), 0)}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(Number(row.article_webinar_attendees), 0)}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(Number(row.article_plx_orders), 0)}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(Number(row.article_plx_revenue), 2)} €
                    </TableCell>
                  </TableRow>
                ))}
                {totals && (
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Insgesamt</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatNumber(totals.doi_webinar_regs, 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatNumber(totals.doi_webinar_attendees, 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatNumber(totals.doi_plx_orders, 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatNumber(totals.doi_plx_revenue, 2)} €
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatNumber(totals.article_webinar_regs, 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatNumber(totals.article_webinar_attendees, 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatNumber(totals.article_plx_orders, 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatNumber(totals.article_plx_revenue, 2)} €
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

function HeaderCell({
  children,
  align,
  tooltip,
}: {
  children: React.ReactNode;
  align?: 'right' | 'left';
  tooltip?: string;
}) {
  const cell = (
    <TableCell
      sx={{
        backgroundColor: '#e0e0e0 !important',
        fontWeight: 'bold',
        cursor: tooltip ? 'help' : undefined,
      }}
      align={align}
    >
      {children}
    </TableCell>
  );
  if (!tooltip) return cell;
  return (
    <Tooltip title={tooltip} arrow placement="top">
      {cell}
    </Tooltip>
  );
}

function SummaryCard({
  label,
  value,
  tooltip,
}: {
  label: string;
  value: string;
  tooltip?: string;
}) {
  const content = (
    <Card sx={{ backgroundColor: '#ffffff' }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ cursor: tooltip ? 'help' : undefined }}
        >
          {label}
        </Typography>
        <Typography variant="h6" fontWeight="bold">
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
  if (!tooltip) return content;
  return (
    <Tooltip title={tooltip} arrow placement="top">
      {content}
    </Tooltip>
  );
}

export default ConversionsTab;
